/**
 * The visual fallback `react-error-boundary`'s `ErrorBoundary` renders
 * (STEP 12 "Global Error Boundary"). Never shows a raw stack trace to
 * the user in production (mirrors the backend's own
 * `errorHandlerMiddleware` posture exactly — an unhandled error
 * returns a generic message, never leaks internals) — the real
 * error's `message`/`stack` is always logged to the console for
 * whoever's debugging, regardless of environment.
 */
import type { JSX } from 'react';
import { Box, Button, Stack, Typography, Alert, AlertTitle } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { isDevelopment } from '@core/config/env';

export interface ErrorFallbackProps {
  error: unknown;
  resetErrorBoundary: () => void;
}

export function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps): JSX.Element {
  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';

  return (
    <Box
      role="alert"
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      p={4}
    >
      <Stack spacing={2} maxWidth={480} alignItems="center" textAlign="center">
        <Alert severity="error" sx={{ width: '100%' }}>
          <AlertTitle>Something went wrong</AlertTitle>
          {isDevelopment ? message : 'An unexpected error occurred. Please try again or contact support if the problem continues.'}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          If this keeps happening, contact your system administrator with the time this occurred.
        </Typography>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={resetErrorBoundary}>
          Try again
        </Button>
      </Stack>
    </Box>
  );
}
